import classNames from "classnames"
import React, { useState } from "react"

import Cell from "./Cell"
import LayoutHeader, { LayoutHeaderProps } from "../../LayoutComponents/Header"
import LayoutSection from "../../LayoutComponents/LayoutSection"

import Badge from "@components/elements/V2/Badge"
import PillButton from "@components/elements/V2/Buttons/PillButton"
import Typography from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"
import {
  FeatureCellValue,
  FeatureRow,
  PLAN_COMPARISON_BREAKDOWN,
  PLAN_COMPARISON_COLUMNS,
  PLAN_COMPARISON_FEATURE_LABEL,
  PLAN_COMPARISON_SUMMARY_ROWS,
  PlanComparisonGroup,
} from "@constants/V2/planComparison"
import { usePageLayoutState } from "@utils/layout"
import { getBackgroundColorClass } from "@utils/V2/color"

type Props = {
  sectionId: string
  backgroundColor?: Color
  innerBackgroundColor?: Color
  heading?: LayoutHeaderProps
}

const GRID = "grid grid-cols-[2fr_1fr_1fr_1fr]"

const PlusHighlight = ({ className }: { className?: string }) => (
  <div
    aria-hidden
    className={classNames(
      "pointer-events-none absolute inset-y-0 left-[60%] z-0 w-[20%] bg-light-blue/40",
      className
    )}
  />
)

const GroupTitle = ({
  title,
  subtitle,
  textColor,
}: {
  title: string
  subtitle?: string
  textColor: Color
}) => (
  <div className="flex flex-col gap-4 border-b border-gray/60 pb-16 sm:pb-24">
    <Typography
      font="grotesk"
      weight="medium"
      size="subscript-lg"
      color={textColor}
      text={title}
      element="h3"
      className="uppercase"
    />
    {subtitle && (
      <Typography
        font="grotesk"
        weight="book"
        size="subscript-sm"
        color={Color.Charcoal}
        text={subtitle}
        className="opacity-60"
      />
    )}
  </div>
)

const getBadgeText = (value: FeatureCellValue): string | null =>
  typeof value === "object" && "badge" in value ? value.badge : null

const getRateNote = (value: FeatureCellValue): string | null =>
  typeof value === "object" && "rate" in value ? (value.note ?? null) : null

const FeatureRowView = ({
  row,
  textColor,
}: {
  row: FeatureRow
  textColor: Color
}) => {
  const badgeTexts = row.values.map(getBadgeText)
  const uniformBadge = badgeTexts.every(
    (text) => text !== null && text === badgeTexts[0]
  )

  // When every plan shares the same per-transaction note, collapse it into a
  // single line spanning the plan columns on mobile (it stays per-cell at sm+).
  const rateNotes = row.values.map(getRateNote)
  const uniformNote = rateNotes.every(
    (text) => text !== null && text === rateNotes[0]
  )
    ? rateNotes[0]
    : null

  return (
    <div
      className={classNames(
        GRID,
        "items-center border-b border-gray/60 py-16 last:border-b-0",
        uniformNote && "gap-y-4"
      )}
    >
      <div className="flex flex-col pr-16">
        <Typography
          font="grotesk"
          weight="medium"
          size="body-md"
          color={textColor}
          text={row.feature}
        />
        {row.description && (
          <Typography
            font="grotesk"
            weight="book"
            size="subscript-sm"
            color={Color.Charcoal}
            text={row.description}
            className="opacity-60"
          />
        )}
      </div>
      {uniformBadge ? (
        <>
          {/* Mobile: a single badge stretched across all plan columns */}
          <Badge
            text={badgeTexts[0] ?? ""}
            style="solid"
            textColor={Color.White}
            accentColor={Color.Charcoal}
            uppercaseText={false}
            isRounded
            size="subscript-sm"
            className="col-span-3 !w-full text-center sm:hidden"
          />
          {/* Desktop: one badge per plan column */}
          <div className="hidden sm:contents">
            {row.values.map((value, index) => (
              <Cell key={index} value={value} textColor={textColor} />
            ))}
          </div>
        </>
      ) : uniformNote ? (
        <>
          {row.values.map((value, index) => (
            <Cell
              key={index}
              value={value}
              textColor={textColor}
              hideNoteOnMobile
            />
          ))}
          {/* Mobile: one note line spanning all three plan columns */}
          <Typography
            font="grotesk"
            weight="book"
            size="subscript-sm"
            color={Color.Charcoal}
            text={uniformNote}
            className="col-span-3 col-start-2 text-center opacity-80 sm:hidden"
          />
        </>
      ) : (
        row.values.map((value, index) => (
          <Cell key={index} value={value} textColor={textColor} />
        ))
      )}
    </div>
  )
}

const PlanComparisonTable = ({
  sectionId,
  backgroundColor = Color.White,
  innerBackgroundColor = Color.White,
  heading,
}: Props) => {
  const { hasAnchorNavigation } = usePageLayoutState()
  const [showBreakdown, setShowBreakdown] = useState(false)

  const isDark =
    innerBackgroundColor === Color.Black ||
    innerBackgroundColor === Color.Charcoal
  const textColor = isDark ? Color.White : Color.Charcoal

  const stickyTopClass = hasAnchorNavigation
    ? "top-60 sm:top-[64px]"
    : "top-navHeight-xs"

  const renderGroup = (group: PlanComparisonGroup, key: number) => (
    <div key={key} className="border-t border-charcoal/30 pt-24">
      <GroupTitle
        title={group.title}
        subtitle={group.subtitle}
        textColor={textColor}
      />
      <div className="relative isolate">
        <PlusHighlight />
        <div className="relative z-10">
          {group.rows.map((row, index) => (
            <FeatureRowView key={index} row={row} textColor={textColor} />
          ))}
        </div>
      </div>
    </div>
  )

  return (
    <LayoutSection id={sectionId} primaryBackgroundColor={backgroundColor}>
      {/* On mobile the inner card goes full-bleed, so paint the section's
          surrounding space with the inner background to keep it seamless
          (the card renders on top). Restored to the section background at sm. */}
      <div
        aria-hidden
        className={classNames(
          "pointer-events-none absolute inset-0 z-0 sm:hidden",
          getBackgroundColorClass(innerBackgroundColor)
        )}
      />
      <div
        className={classNames(
          "relative z-10 -mx-gutter-xs rounded-none px-24 pb-0 pt-40 sm:mx-0 sm:rounded-32 sm:px-48 sm:pb-48 sm:pt-48",
          getBackgroundColorClass(innerBackgroundColor)
        )}
      >
        {heading && <LayoutHeader {...heading} />}

        {/* Sticky header — pins for the full length of the component */}
        <div
          className={classNames(
            getBackgroundColorClass(innerBackgroundColor),
            "sticky z-20 pt-20",
            stickyTopClass
          )}
        >
          <div
            className={classNames(
              GRID,
              "items-stretch border-b border-charcoal/30"
            )}
          >
            <Typography
              font="grotesk"
              weight="medium"
              size="lead-sm"
              color={textColor}
              text={PLAN_COMPARISON_FEATURE_LABEL}
              className="flex items-end pb-16 pt-24 sm:pt-[10px]"
            />
            {PLAN_COMPARISON_COLUMNS.map((column, index) => (
              <div
                key={index}
                className={classNames(
                  "flex items-end justify-center rounded-t-16 pb-16 pt-24 sm:pt-24",
                  column.highlighted && "bg-light-blue/40"
                )}
              >
                <Typography
                  font="grotesk"
                  weight="book"
                  size="lead-md"
                  color={textColor}
                  text={column.name}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Summary — always visible; highlight connects to the header */}
        <div className="relative isolate">
          <PlusHighlight />
          <div className="relative z-10">
            {PLAN_COMPARISON_SUMMARY_ROWS.map((row, index) => (
              <FeatureRowView key={index} row={row} textColor={textColor} />
            ))}
          </div>
        </div>

        {/* Full breakdown — hidden until toggled */}
        {showBreakdown &&
          PLAN_COMPARISON_BREAKDOWN.map((group, index) =>
            renderGroup(group, index)
          )}

        {/* Global toggle — stacks above the sticky header (z-20) */}
        <div className="relative z-30 flex justify-center border-t border-charcoal/30 bg-white pt-32 sm:pt-48">
          <PillButton
            style="bordered"
            size="medium"
            color={Color.Blue}
            overrideTextColor={Color.Black}
            hideIcon
            className="mx-24 !w-full !rounded-[15px] !py-20"
            text={
              showBreakdown
                ? "Click to see less"
                : "Click to see full breakdown"
            }
            onClick={() => setShowBreakdown((previous) => !previous)}
          />
        </div>
      </div>
    </LayoutSection>
  )
}

export default PlanComparisonTable
